import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiCommonService } from '@api-services/api-common.service';
import { ApiAlertError } from '@decorators/api-alert-error';
import { GI_API_ROUTES } from '@gi/api/routes';
import {
  ContractEmailContentTemplate,
  ContractEmailContentTemplateJson
} from '@gi/models/contract-email-content-template';
import { HttpHelper } from '@helpers/http.helper';

@Injectable({
  providedIn: 'root'
})
export class ContractEmailTemplateApiService extends ApiCommonService<ContractEmailContentTemplate> {
  resourceUrl = GI_API_ROUTES.partnerEmailTemplates;

  fromJson(json: ContractEmailContentTemplateJson): ContractEmailContentTemplate {
    return ContractEmailContentTemplate.fromJson(json);
  }

  toJson(model: ContractEmailContentTemplate): Omit<ContractEmailContentTemplateJson, 'archived_at'> {
    return ContractEmailContentTemplate.toJson(model);
  }

  @ApiAlertError()
  getEmailTemplates(partnerId: number): Observable<ContractEmailContentTemplate[]> {
    const url = HttpHelper.replacePartnerUrl(this.resourceUrl, partnerId);
    return this.getAll(null, url);
  }

  @ApiAlertError()
  getEmailTemplate(id: number, partnerId: number): Observable<ContractEmailContentTemplate> {
    const url = HttpHelper.replacePartnerUrl(`${this.resourceUrl}/${id}`, partnerId);
    return this.getById(id, url);
  }

  @ApiAlertError()
  createEmailTemplate(
    emailTemplate: ContractEmailContentTemplate,
    partnerId: number
  ): Observable<ContractEmailContentTemplate> {
    const url = HttpHelper.replacePartnerUrl(this.resourceUrl, partnerId);
    return this.create(emailTemplate, url);
  }

  @ApiAlertError()
  updateEmailTemplate(
    emailTemplate: ContractEmailContentTemplate,
    partnerId: number
  ): Observable<ContractEmailContentTemplate> {
    const url = HttpHelper.replacePartnerUrl(`${this.resourceUrl}/${emailTemplate.id}`, partnerId);
    return this.update(emailTemplate.id, emailTemplate, url);
  }

  @ApiAlertError()
  archiveEmailTemplate(id: number, partnerId: number): Observable<ContractEmailContentTemplate> {
    const url = HttpHelper.replacePartnerUrl(`${this.resourceUrl}/${id}/archive`, partnerId);
    return this.http
      .post<ContractEmailContentTemplateJson>(url, null)
      .pipe(map((json: ContractEmailContentTemplateJson) => this.fromJson(json)));
  }

  @ApiAlertError()
  unarchiveEmailTemplate(id: number, partnerId: number): Observable<ContractEmailContentTemplate> {
    const url = HttpHelper.replacePartnerUrl(`${this.resourceUrl}/${id}/unarchive`, partnerId);
    return this.http
      .post<ContractEmailContentTemplateJson>(url, null)
      .pipe(map((json: ContractEmailContentTemplateJson) => this.fromJson(json)));
  }

  @ApiAlertError()
  setDefaultEmailTemplate(id: number, partnerId: number): Observable<void> {
    const url = HttpHelper.replacePartnerUrl(`${this.resourceUrl}/${id}/default`, partnerId);
    return this.http.post<void>(url, null);
  }
}
